import {
  BuildTeamMember,
  BuildProject,
  BuildProjectFull,
  BuildItem,
  BuildItemFull,
  BuildAssignment,
  BuildSchematic,
  BuildComment,
  BuildTag,
  BuildServerLink,
  BuildModpackLink,
  BuildActivityLogEntry,
  BuildProjectLink,
  BuildDashboardStats,
  BuildTicketLink,
  BuildPaginatedResponse,
  BuildProjectMedia,
  BuildSchematicBulkUploadResult,
  CreateProjectRequest,
  UpdateProjectRequest,
  CreateBuildItemRequest,
  UpdateBuildItemRequest,
  CreateTeamMemberRequest,
  UpdateTeamMemberRequest,
  CreateAssignmentRequest,
  CreateBuildServerLinkRequest,
  UpdateBuildServerLinkRequest,
  CreateBuildModpackLinkRequest,
  UpdateBuildModpackLinkRequest,
  CreateBuildTagRequest,
  UpdateBuildTagRequest,
  ProjectListParams,
  BuildItemListParams,
  TeamMemberRole,
  TeamMemberStatus,
  AssignmentRole,
  AssignmentStatus,
  ProjectLinkType,
  BuildCalendarLink,
} from '../types/buildTeam';
import { CalendarEvent } from '../types/calendar';

const BUILD_API_BASE_URL = 'https://spy.playcdu.co/api/notes/builds';

class BuildTeamApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${BUILD_API_BASE_URL}${endpoint}`, {
      ...options,
      // Cloudflare Access handles authentication via cookies
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  private async requestRaw(endpoint: string, options?: RequestInit): Promise<Response> {
    const response = await fetch(`${BUILD_API_BASE_URL}${endpoint}`, {
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response;
  }

  private buildQueryString(params: Record<string, string | number | boolean | undefined | null>): string {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        qs.append(key, String(value));
      }
    }
    const str = qs.toString();
    return str ? `?${str}` : '';
  }

  // ── Team Member Endpoints (10) ──────────────────────────────────────────

  async createTeamMember(data: CreateTeamMemberRequest): Promise<BuildTeamMember> {
    return this.request('/team', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async listTeamMembers(params?: {
    role?: TeamMemberRole;
    status?: TeamMemberStatus;
    page?: number;
    limit?: number;
  }): Promise<BuildPaginatedResponse<BuildTeamMember> | BuildTeamMember[]> {
    const qs = this.buildQueryString(params || {});
    return this.request(`/team${qs}`);
  }

  async searchTeamMembers(q: string): Promise<BuildTeamMember[]> {
    const qs = this.buildQueryString({ q });
    return this.request(`/team/search${qs}`);
  }

  async getTeamMemberCount(): Promise<{ count: number }> {
    return this.request('/team/count');
  }

  async getTeamMemberByUuid(uuid: string): Promise<BuildTeamMember> {
    return this.request(`/team/uuid/${encodeURIComponent(uuid)}`);
  }

  async getTeamMemberById(id: string): Promise<BuildTeamMember> {
    return this.request(`/team/${encodeURIComponent(id)}`);
  }

  async getTeamMemberAssignments(id: string, status?: AssignmentStatus): Promise<BuildAssignment[]> {
    const qs = this.buildQueryString({ status });
    return this.request(`/team/${encodeURIComponent(id)}/assignments${qs}`);
  }

  async getTeamMemberActivity(id: string, page?: number, limit?: number): Promise<BuildActivityLogEntry[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/team/${encodeURIComponent(id)}/activity${qs}`);
  }

  async updateTeamMember(id: string, data: UpdateTeamMemberRequest): Promise<BuildTeamMember> {
    return this.request(`/team/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteTeamMember(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/team/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // ── Project Endpoints (15) ──────────────────────────────────────────────

  async createProject(data: CreateProjectRequest): Promise<BuildProject> {
    return this.request('/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async listProjects(params?: ProjectListParams): Promise<BuildPaginatedResponse<BuildProject> | BuildProject[]> {
    const qs = this.buildQueryString((params as Record<string, string | number | boolean | undefined>) || {});
    return this.request(`/projects${qs}`);
  }

  async searchProjects(q: string, page?: number, limit?: number): Promise<BuildProject[]> {
    const qs = this.buildQueryString({ q, page, limit });
    return this.request(`/projects/search${qs}`);
  }

  async countProjects(status?: string): Promise<{ count: number }> {
    const qs = this.buildQueryString({ status });
    return this.request(`/projects/count${qs}`);
  }

  async getProjectStats(): Promise<BuildDashboardStats> {
    return this.request('/projects/stats');
  }

  async getProjectById(id: string): Promise<BuildProjectFull> {
    return this.request(`/projects/${encodeURIComponent(id)}`);
  }

  async updateProject(id: string, data: UpdateProjectRequest): Promise<BuildProject> {
    return this.request(`/projects/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async changeProjectStatus(id: string, status: string, reason?: string): Promise<BuildProject> {
    return this.request(`/projects/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, reason }),
    });
  }

  async deleteProject(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/projects/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async getBuildsForProject(id: string, page?: number, limit?: number): Promise<BuildItem[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/projects/${encodeURIComponent(id)}/builds${qs}`);
  }

  async linkProjects(id: string, targetId: string, linkType?: ProjectLinkType): Promise<{ ok: boolean }> {
    return this.request(`/projects/${encodeURIComponent(id)}/links/${encodeURIComponent(targetId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ link_type: linkType }),
    });
  }

  async unlinkProjects(id: string, targetId: string): Promise<{ ok: boolean }> {
    return this.request(`/projects/${encodeURIComponent(id)}/links/${encodeURIComponent(targetId)}`, {
      method: 'DELETE',
    });
  }

  async getLinkedProjects(id: string): Promise<{ outgoing: BuildProjectLink[]; incoming: BuildProjectLink[] }> {
    return this.request(`/projects/${encodeURIComponent(id)}/links`);
  }

  async getProjectActivity(id: string, page?: number, limit?: number): Promise<BuildActivityLogEntry[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/projects/${encodeURIComponent(id)}/activity${qs}`);
  }

  // ── Build Item Endpoints (13) ───────────────────────────────────────────

  async createBuildItem(data: CreateBuildItemRequest): Promise<BuildItem> {
    return this.request('/items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async listBuildItems(params?: BuildItemListParams): Promise<BuildPaginatedResponse<BuildItem> | BuildItem[]> {
    const qs = this.buildQueryString((params as Record<string, string | number | boolean | undefined>) || {});
    return this.request(`/items${qs}`);
  }

  async searchBuildItems(q: string, page?: number, limit?: number): Promise<BuildItem[]> {
    const qs = this.buildQueryString({ q, page, limit });
    return this.request(`/items/search${qs}`);
  }

  async countBuildItems(status?: string, projectId?: string): Promise<{ count: number }> {
    const qs = this.buildQueryString({ status, project_id: projectId });
    return this.request(`/items/count${qs}`);
  }

  async getBuildItemById(id: string): Promise<BuildItemFull> {
    return this.request(`/items/${encodeURIComponent(id)}`);
  }

  async updateBuildItem(id: string, data: UpdateBuildItemRequest): Promise<BuildItem> {
    return this.request(`/items/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async changeBuildItemStatus(id: string, status: string, reason?: string): Promise<BuildItem> {
    return this.request(`/items/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, reason }),
    });
  }

  async moveBuildItemProject(id: string, projectId: string | null): Promise<BuildItem> {
    return this.request(`/items/${encodeURIComponent(id)}/project`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_id: projectId }),
    });
  }

  async deleteBuildItem(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/items/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async getBuildItemActivity(id: string, page?: number, limit?: number): Promise<BuildActivityLogEntry[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/items/${encodeURIComponent(id)}/activity${qs}`);
  }

  async getRecentActivity(limit?: number): Promise<BuildActivityLogEntry[]> {
    const qs = this.buildQueryString({ limit });
    return this.request(`/activity/recent${qs}`);
  }

  async getActivityByActor(actorId: string, page?: number, limit?: number): Promise<BuildActivityLogEntry[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/activity/by/${encodeURIComponent(actorId)}${qs}`);
  }

  // ── Assignment Endpoints (7) ────────────────────────────────────────────

  async assignToProject(projectId: string, data: CreateAssignmentRequest): Promise<BuildAssignment> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/assignments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getProjectAssignments(projectId: string): Promise<BuildAssignment[]> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/assignments`);
  }

  async assignToBuild(buildId: string, data: CreateAssignmentRequest): Promise<BuildAssignment> {
    return this.request(`/items/${encodeURIComponent(buildId)}/assignments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getBuildAssignments(buildId: string): Promise<BuildAssignment[]> {
    return this.request(`/items/${encodeURIComponent(buildId)}/assignments`);
  }

  async removeAssignment(assignmentId: string): Promise<{ ok: boolean }> {
    return this.request(`/assignments/${encodeURIComponent(assignmentId)}`, {
      method: 'DELETE',
    });
  }

  async updateAssignment(assignmentId: string, data: { role?: AssignmentRole; status?: AssignmentStatus }): Promise<BuildAssignment> {
    return this.request(`/assignments/${encodeURIComponent(assignmentId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async completeAssignment(assignmentId: string): Promise<BuildAssignment> {
    return this.request(`/assignments/${encodeURIComponent(assignmentId)}/complete`, {
      method: 'PATCH',
    });
  }

  // ── Schematic Endpoints (15) ────────────────────────────────────────────

  /**
   * Single-file upload returns one BuildSchematic; multiple files return { created, failed }.
   */
  async uploadSchematic(
    formData: FormData
  ): Promise<BuildSchematic | BuildSchematicBulkUploadResult> {
    const response = await fetch(`${BUILD_API_BASE_URL}/schematics/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  }

  async listSchematics(page?: number, limit?: number): Promise<BuildSchematic[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/schematics${qs}`);
  }

  async searchSchematics(q: string): Promise<BuildSchematic[]> {
    const qs = this.buildQueryString({ q });
    return this.request(`/schematics/search${qs}`);
  }

  async getSchematicCount(): Promise<{ count: number }> {
    return this.request('/schematics/count');
  }

  async getSchematicById(id: string): Promise<BuildSchematic> {
    return this.request(`/schematics/${encodeURIComponent(id)}`);
  }

  async getSchematicsByUploader(uploaderId: string): Promise<BuildSchematic[]> {
    return this.request(`/schematics/uploader/${encodeURIComponent(uploaderId)}`);
  }

  async downloadSchematic(id: string): Promise<Response> {
    return this.requestRaw(`/schematics/${encodeURIComponent(id)}/download`);
  }

  async downloadProjectMedia(mediaId: string): Promise<Response> {
    return this.requestRaw(`/projects/images/${encodeURIComponent(mediaId)}/download`);
  }

  async updateSchematic(id: string, data: { title?: string | null; version?: string | null; description?: string | null; metadata?: string | null }): Promise<BuildSchematic> {
    return this.request(`/schematics/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteSchematic(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/schematics/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async linkSchematicToProject(projectId: string, schematicId: string): Promise<{ ok: boolean }> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/schematics/${encodeURIComponent(schematicId)}`, {
      method: 'POST',
    });
  }

  async unlinkSchematicFromProject(projectId: string, schematicId: string): Promise<{ ok: boolean }> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/schematics/${encodeURIComponent(schematicId)}`, {
      method: 'DELETE',
    });
  }

  async getProjectSchematics(projectId: string): Promise<BuildSchematic[]> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/schematics`);
  }

  async linkSchematicToBuild(buildId: string, schematicId: string): Promise<{ ok: boolean }> {
    return this.request(`/items/${encodeURIComponent(buildId)}/schematics/${encodeURIComponent(schematicId)}`, {
      method: 'POST',
    });
  }

  async unlinkSchematicFromBuild(buildId: string, schematicId: string): Promise<{ ok: boolean }> {
    return this.request(`/items/${encodeURIComponent(buildId)}/schematics/${encodeURIComponent(schematicId)}`, {
      method: 'DELETE',
    });
  }

  async getBuildSchematics(buildId: string): Promise<BuildSchematic[]> {
    return this.request(`/items/${encodeURIComponent(buildId)}/schematics`);
  }

  // ── Project media (gallery) ─────────────────────────────────────────────

  async listProjectImages(
    projectId: string,
    page?: number,
    limit?: number
  ): Promise<BuildProjectMedia[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/projects/${encodeURIComponent(projectId)}/images${qs}`);
  }

  async uploadProjectImages(projectId: string, formData: FormData): Promise<BuildProjectMedia | BuildProjectMedia[]> {
    const response = await fetch(`${BUILD_API_BASE_URL}/projects/${encodeURIComponent(projectId)}/images/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  }

  async updateProjectMediaCaption(
    mediaId: string,
    data: { caption?: string | null }
  ): Promise<BuildProjectMedia> {
    return this.request(`/projects/images/${encodeURIComponent(mediaId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteProjectMedia(mediaId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/projects/images/${encodeURIComponent(mediaId)}`, {
      method: 'DELETE',
    });
  }

  // ── Comment Endpoints (7) ───────────────────────────────────────────────

  async addProjectComment(projectId: string, content: string): Promise<BuildComment> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
  }

  async addProjectCommentWithImages(projectId: string, formData: FormData): Promise<BuildComment> {
    const response = await fetch(`${BUILD_API_BASE_URL}/projects/${encodeURIComponent(projectId)}/comments`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  }

  async getProjectComments(projectId: string, page?: number, limit?: number): Promise<BuildComment[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/projects/${encodeURIComponent(projectId)}/comments${qs}`);
  }

  async addBuildComment(buildId: string, content: string): Promise<BuildComment> {
    return this.request(`/items/${encodeURIComponent(buildId)}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
  }

  async addBuildCommentWithImages(buildId: string, formData: FormData): Promise<BuildComment> {
    const response = await fetch(`${BUILD_API_BASE_URL}/items/${encodeURIComponent(buildId)}/comments`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  }

  async getBuildComments(buildId: string, page?: number, limit?: number): Promise<BuildComment[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/items/${encodeURIComponent(buildId)}/comments${qs}`);
  }

  async getCommentById(commentId: string): Promise<BuildComment> {
    return this.request(`/comments/${encodeURIComponent(commentId)}`);
  }

  async updateComment(commentId: string, content: string): Promise<BuildComment> {
    return this.request(`/comments/${encodeURIComponent(commentId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
  }

  async deleteComment(commentId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/comments/${encodeURIComponent(commentId)}`, {
      method: 'DELETE',
    });
  }

  // ── Tag Endpoints (11) ──────────────────────────────────────────────────

  async createTag(data: CreateBuildTagRequest): Promise<BuildTag> {
    return this.request('/tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async listTags(): Promise<BuildTag[]> {
    return this.request('/tags');
  }

  async getTagById(id: string): Promise<BuildTag> {
    return this.request(`/tags/${encodeURIComponent(id)}`);
  }

  async updateTag(id: string, data: UpdateBuildTagRequest): Promise<BuildTag> {
    return this.request(`/tags/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteTag(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/tags/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async tagProject(projectId: string, tagId: string): Promise<{ ok: boolean }> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/tags/${encodeURIComponent(tagId)}`, {
      method: 'POST',
    });
  }

  async untagProject(projectId: string, tagId: string): Promise<{ ok: boolean }> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/tags/${encodeURIComponent(tagId)}`, {
      method: 'DELETE',
    });
  }

  async getProjectTags(projectId: string): Promise<BuildTag[]> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/tags`);
  }

  async tagBuild(buildId: string, tagId: string): Promise<{ ok: boolean }> {
    return this.request(`/items/${encodeURIComponent(buildId)}/tags/${encodeURIComponent(tagId)}`, {
      method: 'POST',
    });
  }

  async untagBuild(buildId: string, tagId: string): Promise<{ ok: boolean }> {
    return this.request(`/items/${encodeURIComponent(buildId)}/tags/${encodeURIComponent(tagId)}`, {
      method: 'DELETE',
    });
  }

  async getBuildTags(buildId: string): Promise<BuildTag[]> {
    return this.request(`/items/${encodeURIComponent(buildId)}/tags`);
  }

  // ── Server Link Endpoints (9) ───────────────────────────────────────────

  async linkServerToProject(projectId: string, data: CreateBuildServerLinkRequest): Promise<BuildServerLink> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/servers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getProjectServers(projectId: string): Promise<BuildServerLink[]> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/servers`);
  }

  async linkServerToBuild(buildId: string, data: CreateBuildServerLinkRequest): Promise<BuildServerLink> {
    return this.request(`/items/${encodeURIComponent(buildId)}/servers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getBuildServers(buildId: string): Promise<BuildServerLink[]> {
    return this.request(`/items/${encodeURIComponent(buildId)}/servers`);
  }

  async updateServerLink(linkId: string, data: UpdateBuildServerLinkRequest): Promise<BuildServerLink> {
    return this.request(`/servers/${encodeURIComponent(linkId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteServerLink(linkId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/servers/${encodeURIComponent(linkId)}`, {
      method: 'DELETE',
    });
  }

  async getServerLinksByFqdn(fqdn: string): Promise<BuildServerLink[]> {
    return this.request(`/servers/fqdn/${encodeURIComponent(fqdn)}`);
  }

  async getServerLinksByCluster(clusterId: string): Promise<BuildServerLink[]> {
    return this.request(`/servers/cluster/${encodeURIComponent(clusterId)}`);
  }

  async listAllServerLinks(page?: number, limit?: number): Promise<BuildServerLink[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/servers${qs}`);
  }

  // ── Modpack Link Endpoints (8) ──────────────────────────────────────────

  async linkModpackToProject(projectId: string, data: CreateBuildModpackLinkRequest): Promise<BuildModpackLink> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/modpacks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getProjectModpacks(projectId: string): Promise<BuildModpackLink[]> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/modpacks`);
  }

  async linkModpackToBuild(buildId: string, data: CreateBuildModpackLinkRequest): Promise<BuildModpackLink> {
    return this.request(`/items/${encodeURIComponent(buildId)}/modpacks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getBuildModpacks(buildId: string): Promise<BuildModpackLink[]> {
    return this.request(`/items/${encodeURIComponent(buildId)}/modpacks`);
  }

  async updateModpackLink(linkId: string, data: UpdateBuildModpackLinkRequest): Promise<BuildModpackLink> {
    return this.request(`/modpacks/${encodeURIComponent(linkId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteModpackLink(linkId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/modpacks/${encodeURIComponent(linkId)}`, {
      method: 'DELETE',
    });
  }

  async getModpackLinksByName(name: string): Promise<BuildModpackLink[]> {
    return this.request(`/modpacks/name/${encodeURIComponent(name)}`);
  }

  async listAllModpackLinks(page?: number, limit?: number): Promise<BuildModpackLink[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/modpacks${qs}`);
  }

  // ── Calendar Link Endpoints (9) ─────────────────────────────────────────

  async linkProjectToCalendarEvent(projectId: string, eventId: string): Promise<{ ok: boolean }> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/calendar/${encodeURIComponent(eventId)}`, {
      method: 'POST',
    });
  }

  async unlinkProjectFromCalendarEvent(projectId: string, eventId: string): Promise<{ ok: boolean }> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/calendar/${encodeURIComponent(eventId)}`, {
      method: 'DELETE',
    });
  }

  async getProjectCalendarEvents(projectId: string): Promise<CalendarEvent[]> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/calendar`);
  }

  async getProjectUpcomingEvents(projectId: string): Promise<CalendarEvent[]> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/calendar/upcoming`);
  }

  async linkBuildToCalendarEvent(buildId: string, eventId: string): Promise<{ ok: boolean }> {
    return this.request(`/items/${encodeURIComponent(buildId)}/calendar/${encodeURIComponent(eventId)}`, {
      method: 'POST',
    });
  }

  async unlinkBuildFromCalendarEvent(buildId: string, eventId: string): Promise<{ ok: boolean }> {
    return this.request(`/items/${encodeURIComponent(buildId)}/calendar/${encodeURIComponent(eventId)}`, {
      method: 'DELETE',
    });
  }

  async getBuildCalendarEvents(buildId: string): Promise<CalendarEvent[]> {
    return this.request(`/items/${encodeURIComponent(buildId)}/calendar`);
  }

  async getBuildUpcomingEvents(buildId: string): Promise<CalendarEvent[]> {
    return this.request(`/items/${encodeURIComponent(buildId)}/calendar/upcoming`);
  }

  async getBuildsAndProjectsForEvent(eventId: string): Promise<BuildCalendarLink[]> {
    return this.request(`/calendar/event/${encodeURIComponent(eventId)}`);
  }

  // ── Ticket Link Endpoints (7) ───────────────────────────────────────────

  async linkTicketToProject(projectId: string, ticketId: number): Promise<{ ok: boolean }> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/tickets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket_id: ticketId }),
    });
  }

  async unlinkTicketFromProject(projectId: string, ticketId: number): Promise<{ ok: boolean }> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/tickets/${ticketId}`, {
      method: 'DELETE',
    });
  }

  async getProjectTickets(projectId: string): Promise<BuildTicketLink[]> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/tickets`);
  }

  async linkTicketToBuild(buildId: string, ticketId: number): Promise<{ ok: boolean }> {
    return this.request(`/items/${encodeURIComponent(buildId)}/tickets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket_id: ticketId }),
    });
  }

  async unlinkTicketFromBuild(buildId: string, ticketId: number): Promise<{ ok: boolean }> {
    return this.request(`/items/${encodeURIComponent(buildId)}/tickets/${ticketId}`, {
      method: 'DELETE',
    });
  }

  async getBuildTickets(buildId: string): Promise<BuildTicketLink[]> {
    return this.request(`/items/${encodeURIComponent(buildId)}/tickets`);
  }

  async getTicketsForTicketId(ticketId: number): Promise<BuildTicketLink[]> {
    return this.request(`/tickets/${ticketId}`);
  }
}

export const buildTeamApiService = new BuildTeamApiService();
